import { useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Draggable } from 'apps/studio/components/dragAndDrop/Draggable'
import { FieldIcon } from 'apps/studio/components/shared/FieldIcon'
import {
  createElement,
  createTable,
} from 'apps/studio/store/actions/sharedActions'
import { alphaEntryTypesSelector } from 'apps/formsCreationKit/store/selectors/types'
import { studioTablesEnabledSelector } from 'apps/studio/store/selectors/featureFlags'
import {
  createTableData,
  buildTextEntryType,
  STATIC_TABLE_WIDTH_RATIO,
  STATIC_TABLE_ROW_HEIGHT,
  STATIC_TABLE_TITLE_HEIGHT,
  STATIC_TABLE_HEADER_ROWS,
  STATIC_TABLE_DATA_ROWS,
} from 'apps/studio/lib/utils/entities'
import {
  activePageSelector,
  pagesSelector,
  elementsSelector,
  pageDefaultPositionSelector,
  layoutsSelector,
} from 'apps/studio/store/selectors/pdf'
import { pdfPageMetaSelector } from 'apps/studio/store/selectors/pdfPageMeta'
import {
  handleDataInputDrop,
  selectActiveEntryLabel,
} from 'apps/studio/lib/utils/dnd'
import { getTypeDisplayName } from 'apps/studio/lib/utils/entryTypes'
import {
  sheetsSelector,
  sectionsSelector,
} from 'apps/formsCreationKit/store/selectors/entities'

const calculateCenterPosition = (
  pageBounds,
  scaleFactor,
  defaultElementPosition
) => {
  if (!pageBounds) return { ...defaultElementPosition, x: 0, y: 0 }

  return {
    ...defaultElementPosition,
    x: Math.round(
      pageBounds.width / scaleFactor / 2 - defaultElementPosition.width / 2
    ),
    y: Math.round(
      pageBounds.height / scaleFactor / 2 - defaultElementPosition.height / 2
    ),
  }
}

const buildCreateElementPayload = (targetPageData, entryData, elements) => {
  const {
    existingTargetScreenData,
    targetPage,
    targetPageMeta,
    targetPageLayout,
  } = targetPageData
  const {
    pageDefaultPosition: defaultElementPosition,
    scaleFactor,
    pageNumber,
  } = targetPageMeta
  const pageElement = document.querySelector(
    `[data-page-number="${pageNumber}"]`
  )
  const pageBounds = pageElement?.getBoundingClientRect()

  const draggable = {
    data: { current: { entryData } },
  }

  const droppable = {
    data: {
      current: {
        page: targetPage,
        layout: targetPageLayout,
        existingScreenData: existingTargetScreenData,
      },
    },
  }
  const calculatedPosition = calculateCenterPosition(
    pageBounds,
    scaleFactor,
    defaultElementPosition
  )

  const payload = handleDataInputDrop(
    draggable,
    droppable,
    elements,
    calculatedPosition
  )
  return payload
}

const useTargetPageData = () => {
  const allLayouts = useSelector(layoutsSelector)
  const allSections = useSelector(sectionsSelector)
  const allSheets = useSelector(sheetsSelector)
  const allPdfPageMeta = useSelector(pdfPageMetaSelector)
  const allPages = useSelector(pagesSelector)
  const activePage = useSelector(activePageSelector)

  // The target page is either the active page or the last page when no page is active
  const targetPageNumber = activePage?.page_number || allPdfPageMeta.length
  const targetPage = allPages.find(
    ({ page_number }) => page_number === targetPageNumber
  )
  const targetPageMeta = allPdfPageMeta.find(
    ({ pageNumber }) => pageNumber === targetPageNumber
  )
  // TODO: Create a selector that takes a page number and gets the corresponding layout, section, and sheet data instead of doing this lookup here and other places.
  const targetPageLayout = allLayouts.find(
    ({ page_id, parent_layout_id }) =>
      page_id === targetPage?.id && !parent_layout_id
  )
  const targetPageSection = allSections.find(
    section => section.id === targetPageLayout?.section_id
  )
  const targetPageSheet =
    allSheets.find(sheet => sheet.section_id === targetPageSection?.id) || {}
  const { id: sheetId } = targetPageSheet
  const pageDefaultPosition = useSelector(
    pageDefaultPositionSelector(targetPageNumber)
  )

  return {
    existingTargetScreenData: sheetId
      ? {
          section: targetPageSection,
          sheet: targetPageSheet,
        }
      : null,
    targetPageMeta: {
      ...targetPageMeta,
      pageDefaultPosition,
    },
    targetPageLayout,
    targetPage,
  }
}

const DataInputItem = ({ ...entryType }) => {
  const dispatch = useDispatch()
  const elements = useSelector(elementsSelector)
  const targetPageData = useTargetPageData()
  const { type, id } = entryType
  const entryData = {
    ...entryType,
    entry_type_id: id,
  }

  return (
    <Draggable
      id={`data-input-${type.replace(/\s+/g, '')}`}
      data={{
        type: 'dataInput',
        entryData,
      }}
      classes="data-input--option gcds--item item--condensed"
      onClick={() => {
        const payload = buildCreateElementPayload(
          targetPageData,
          entryData,
          elements
        )
        dispatch(createElement(payload))
        window.requestAnimationFrame(() => {
          setTimeout(selectActiveEntryLabel, 50, type)
        })
      }}
    >
      <div className="item--icon">
        <FieldIcon type={type} />
      </div>
      {getTypeDisplayName(type)}
    </Draggable>
  )
}

const TableInputItem = () => {
  const dispatch = useDispatch()
  const entryTypes = useSelector(alphaEntryTypesSelector)
  const { targetPage, targetPageLayout, targetPageMeta } = useTargetPageData()
  const entryTypePayload = useMemo(
    () => buildTextEntryType(entryTypes),
    [entryTypes]
  )
  const tablePosition = useMemo(() => {
    const originalWidth = targetPageMeta?.originalWidth

    return {
      width: originalWidth * STATIC_TABLE_WIDTH_RATIO,
      height:
        STATIC_TABLE_TITLE_HEIGHT +
        (STATIC_TABLE_HEADER_ROWS + STATIC_TABLE_DATA_ROWS) *
          STATIC_TABLE_ROW_HEIGHT,
    }
  }, [targetPageMeta?.originalWidth])
  const draggableData = useMemo(
    () => ({ type: 'table', entryType: entryTypePayload, tablePosition }),
    [entryTypePayload, tablePosition]
  )

  const onAdd = () => {
    if (!targetPage || !targetPageLayout || !entryTypePayload) return

    const pageElement = document.querySelector(
      `[data-page-number="${targetPageMeta.pageNumber}"]`
    )
    const bounds = pageElement?.getBoundingClientRect()
    const scaleFactor = targetPageMeta.scaleFactor || 1
    const pageWidth = bounds ? bounds.width / scaleFactor : 0
    const pageHeight = bounds ? bounds.height / scaleFactor : 0
    if (!pageWidth) return

    const payload = createTableData({
      pageId: targetPage.id,
      pageNumber: targetPageMeta.pageNumber,
      rootLayout: targetPageLayout,
      pageWidth,
      pageHeight,
      entryType: entryTypePayload,
      // TODO: Add sectionPosition. Leaving this out for now as positioning is kind of weird and needs to be thought through better for both normal and table screens
      // sectionPosition,
    })

    dispatch(createTable({ ...payload, page: targetPage }))
  }

  return (
    <Draggable
      id="data-input-Table"
      data={draggableData}
      classes="data-input--option gcds--item item--condensed"
      onClick={onAdd}
    >
      <div className="item--icon">
        <FieldIcon type="Static Table" />
      </div>
      Static Table
    </Draggable>
  )
}

// TODO: We might benefit from standardizing the panel markup into it's
// own component now that we can see where the repeated markup occurs
export const AddLibrary = () => {
  const entryTypes = useSelector(alphaEntryTypesSelector)
  const tablesEnabled = useSelector(studioTablesEnabledSelector)

  const dataInputItems = useMemo(() => {
    const items = entryTypes.map(entryType => ({
      key: `data-input-${entryType.type}`,
      sortKey: (entryType.type || '').toLowerCase(),
      Component: DataInputItem,
      props: entryType,
    }))
    if (tablesEnabled) {
      items.push({
        key: 'static-table',
        sortKey: 'static table',
        Component: TableInputItem,
        props: {},
      })
    }
    return items.sort((a, b) => a.sortKey.localeCompare(b.sortKey))
  }, [entryTypes, tablesEnabled])

  const addSections = [
    {
      key: 'data-input',
      title: 'DATA INPUT',
      items: dataInputItems,
    },
  ]

  return (
    <div className="gcds--control-panel workspace-panel add--panel">
      <div className="panel--info is-sticky">
        <div className="panel--title">Add</div>
      </div>
      {addSections.map(({ title, items }, index) => (
        <div className="panel--content workspace-panel--section" key={index}>
          <div className="panel--section-title --workspace-panel--section-title">
            {title}
          </div>

          <div className="add--list">
            {items.map(({ key, Component, props }) => (
              <Component key={key} {...props} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
